import React from 'react'
import ReactMarkdown from 'react-markdown'

import { Card } from '@acorns/web-components'
import styled from 'styled-components'

import { Caption } from 'components/typography'
import Feedback from 'pages/grow/GrowArticle/Feedback'
import { AuthorDetails } from 'pages/grow/GrowArticle/Onboarding/AuthorDetails'
import { style } from 'theme'
import {
  GROW_ARTICLE_FOOTNOTE,
  GROW_ARTICLE_HERO_IMAGE,
} from 'utils/testing/testIDs'

import { HeadingRenderer } from './renderers'

type Article = {
  article_type: string
  author?: Author
  content: string
  context?: Context
  display_hero_image: boolean
  feedback?: string[]
  footnote?: string
  id: string
  image: Image
  preview_text: string
  slug: string
  title: string
  up_next?: UpNextItem[]
}

const HeroImage = styled.div<{ imageSrc: string }>`
  background-image: url(${(props) => props.imageSrc});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: ${style((theme) => theme.styles.borderRadius)}
    ${style((theme) => theme.styles.borderRadius)} 0 0;
  height: 260px;
  width: 100%;

  @media (min-width: ${style((theme) => theme.breakpoints.md)}px) {
    height: 340px;
  }
`

const ContentCard = styled(Card)`
  position: relative;
  padding: 0 20px 0 20px;
`

const Container = styled.div`
  padding: 30px 20px;

  @media (min-width: ${style((theme) => theme.breakpoints.sm)}px) {
    padding: 40px;
  }

  @media (min-width: ${style((theme) => theme.breakpoints.md)}px) {
    padding: 50px;
  }
`

const Title = styled.h1`
  color: ${style((theme) => theme.colors.slate)};
  font-size: 26px;
  font-weight: normal;
  line-height: 1.36;
  margin: 0;
`

const StyledMarkdown = styled(ReactMarkdown)`
  color: ${style((theme) => theme.colors.slate)};
  font-size: 16px;
  line-height: 1.57;
  margin-top: 20px;

  a {
    color: ${style((theme) => theme.colors.slate)};
  }

  p {
    margin-top: 0;
    margin-bottom: 40px;

    > img {
      border-radius: ${style((theme) => theme.styles.borderRadius)};
      width: 100%;
    }
  }

  h2 {
    font-size: 18px;
    line-height: 1.5;
    margin-bottom: 6px;
  }

  h3 {
    font-size: 16px;
    line-height: 1.57;
    margin-bottom: 6px;
  }

  h4 {
    margin: 0;
    a {
      color: ${style((theme) => theme.colors.green)};
      text-decoration: none;
    }
  }

  blockquote {
    border-left: 2px solid ${style((theme) => theme.colors.stoneLight)};
    border-radius: 1px;
    font-size: 18px;
    font-style: italic;
    line-height: 1.56;
    margin: 0;
    padding-left: 18px;
  }
`

const Footnote = styled(Caption)`
  line-height: 16px;
`

type Props = {
  article: Article
  showUpNext?: boolean
}

const Content = (props) => {
  return (
    <>
      <ContentCard>
        {props.article.image &&
          props.article.image.url &&
          props.article.display_hero_image && (
            <HeroImage
              data-testid={GROW_ARTICLE_HERO_IMAGE}
              imageSrc={props.article.image.url}
            />
          )}
        <Container>
          <Title>{props.article.title}</Title>
          <StyledMarkdown
            renderers={{
              Heading: HeadingRenderer,
            }}
            source={props.article.content}
          />

          {props.article.footnote && (
            <Footnote data-testid={GROW_ARTICLE_FOOTNOTE}>
              {props.article.footnote}
            </Footnote>
          )}

          {props.article.author && (
            <AuthorDetails author={props.article.author} />
          )}

          <Feedback
            articleId={props.article.id}
            articleTitle={props.article.title}
            articleType={props.article.article_type}
            negativeFeedbackReasons={props.article.feedback}
          />
        </Container>
      </ContentCard>
    </>
  )
}

export default Content
